import { Component, Input, OnInit, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { RoleService } from '../../services/role.service';
import { Role } from '../../model/role.model';
import { Access, Control, grantedCount, ROLE_CREATE_NEEDS_EDIT, RoleAccessLabel, roleAccessLabel } from '../../services/workspaces.util';
import { BUILT_IN_ADMIN_ID, isFullAccessRole } from '../../services/role-rules';
import { toneFor } from './tones';
import { registerScreenActions } from '../../studio-nav/screen-panels.service';
import type { ScreenActionAnswer } from '../../services/mcp/actions/fast-lane';

/**
 * Roles — lifted out of the old owner-only `admin-users.component.ts` (its Roles section, moved
 * here unchanged in behaviour). A role is just a name; what it can reach is set per entity on the
 * Entity → Permissions tab. The `admin` role always exists with full access and is shown locked.
 *
 * A role can no longer be deleted from the editor — the server refuses it too (`manageRole`'s
 * `deleteRole` returns `ROLE_DELETE_REFUSED`), because deleting one would change what live End
 * users can reach.
 */
@Component({
  selector: 'app-roles-tab',
  standalone: true,
  imports: [CommonModule, FormsModule],
  templateUrl: './roles-tab.component.html',
  styleUrl: './roles-tab.component.css',
})
export class RolesTabComponent implements OnInit {
  @Input() access!: Access;

  // Was (2026-10-04, plan 4d Q45 — the id now comes from the shared rule): readonly RESERVED_ADMIN = 'admin';
  readonly RESERVED_ADMIN = BUILT_IN_ADMIN_ID;
  loading = false;
  roleDefs: Role[] = [];

  newRoleName = '';
  creatingRole = false;
  roleError = '';

  private router = inject(Router);
  private offers = registerScreenActions({
    'create-role': (values) => this.createRoleFor(values),
    'rename-role': (values) => this.renameRoleFor(values),
  });

  constructor(private roleService: RoleService) {}

  /** What the name being typed will be saved as — the id the gateway and every claim carry, shown
   *  BEFORE it is created, since it can never be changed afterwards. Empty until there is one. */
  previewId(): string {
    return this.newRoleName.trim() ? this.slug(this.newRoleName) : '';
  }

  /** Data (the entity list) is where a role is actually given its permissions — Entity →
   *  Permissions. The explainer and a zero-permission role both point there, so both link there. */
  openData(): void {
    this.router.navigate(['/dashboard/entity-list']);
  }

  /** The role's colour: the same dot it wears on every person's pill on the Workspaces tab. */
  tone(r: Role): string {
    return toneFor(r.id).soft;
  }

  /** Plan 4d final review (M3) — the template asks the one rule, not `r.id === RESERVED_ADMIN` repeated. */
  isFullAccess(r: Role): boolean {
    return isFullAccessRole(r);
  }

  async ngOnInit(): Promise<void> {
    this.loading = true;
    this.roleDefs = this.sortRoles(await this.roleService.query(1000, false));
    this.loading = false;
  }

  /** Any non-viewer may create a role — `manage-role.function.ts` gates `createRole` on editor, not
   *  owner. Only project-level switches and seat caps are the owner's alone; roles are not named
   *  among them. A viewer never reaches this tab at all (the page's own gate stops them first), so
   *  the disabled branch below is unreachable in practice — it exists only so this control is never
   *  wrong if that changes.
   *
   *  The BEHAVIOUR is unchanged; only the sentence moved (fix-round-3 Minor 6). It had shipped typed
   *  into this getter, which is exactly what `workspaces.util.ts`'s own header rule forbids — every
   *  other refusal on this page reads its copy from there, and one that doesn't is the one that
   *  drifts. */
  get createControl(): Control {
    return this.access === 'viewer'
      ? { enabled: false, reason: ROLE_CREATE_NEEDS_EDIT }
      : { enabled: true, reason: '' };
  }

  /** admin first, then alphabetical by name. */
  private sortRoles(roles: Role[]): Role[] {
    // Was (2026-10-04, plan 4d Q45 — sorts by the shared rule now):
    // a.id === this.RESERVED_ADMIN ? -1 : b.id === this.RESERVED_ADMIN ? 1 : (a.name || '').localeCompare(b.name || '')
    return [...roles].sort((a, b) =>
      isFullAccessRole(a) ? -1 : isFullAccessRole(b) ? 1 : (a.name || '').localeCompare(b.name || ''));
  }

  permCount(r: Role): number {
    // Was (2026-10-06, ws 9a W9): return Object.keys(r.permissions || {}).length;
    return grantedCount(r.permissions);
  }

  /** What the row prints for this role's grant count — the zero-vs-nonzero call lives in the util,
   *  not here (`roleAccessLabel`'s own docblock: zero is fatal, not "unconfigured"). */
  accessLabel(r: Role): RoleAccessLabel {
    return roleAccessLabel(this.permCount(r));
  }

  /** Name → stable, human-readable id: lowercase, non-alphanumerics collapsed to '_'. This is the
   *  string the gateway/claims carry — no {clientId}_ prefix (that was the old broken seed). */
  private slug(name: string): string {
    return name.trim().toLowerCase().replace(/[^a-z0-9]+/g, '_').replace(/^_+|_+$/g, '');
  }

  /** ws 9b-3 W11 — one role card at a time is being renamed. */
  renamingId: string | null = null;
  renameDraft = '';
  renameError = '';
  savingRename = false;

  startRename(r: Role): void {
    if (!this.createControl.enabled || this.isFullAccess(r)) return;
    this.renamingId = r.id; this.renameDraft = r.name; this.renameError = '';
  }
  cancelRename(): void { if (!this.savingRename) { this.renamingId = null; this.renameError = ''; } }
  async saveRename(r: Role): Promise<void> {
    const name = this.renameDraft.trim();
    if (!name) { this.renameError = 'Enter a name.'; return; }
    if (name === r.name) { this.renamingId = null; return; }
    this.savingRename = true; this.renameError = '';
    try {
      await this.roleService.rename(r.id, name);
      this.roleDefs = this.sortRoles(this.roleDefs.map((x) => (x.id === r.id ? ({ ...x, name } as Role) : x)));
      this.renamingId = null;
    } catch (e: any) {
      this.renameError = e?.message || 'Could not rename the role. Please try again.';
    }
    this.savingRename = false;
  }

  async createRole(): Promise<void> {
    this.roleError = '';
    if (!this.createControl.enabled) { this.roleError = this.createControl.reason; return; }
    const name = this.newRoleName.trim();
    if (!name) return;
    const id = this.slug(name);
    if (!id) { this.roleError = 'Enter a name with at least one letter or number.'; return; }
    if (id === this.RESERVED_ADMIN || id === 'inactive') { this.roleError = `"${id}" is reserved — pick another name.`; return; }
    if (this.roleDefs.some((r) => r.id === id)) { this.roleError = `A role with id "${id}" already exists.`; return; }

    this.creatingRole = true;
    try {
      await this.roleService.create({ id, name, permissions: {} } as Role);
      this.newRoleName = '';
      // Was (2026-10-06, ws 9a W9): this.roleDefs = this.sortRoles(await this.roleService.query(1000, false));
      const fresh = await this.roleService.query(1000, false);
      // A failed re-read comes back [] — keep what we have and add the new role locally rather than wiping the list.
      this.roleDefs = this.sortRoles(fresh.length ? fresh : [...this.roleDefs, { id, name, permissions: {} } as Role]);
    } catch (e: any) {
      // Was (2026-10-06, ws 9a W9): this.roleError = 'Could not create the role. Please try again.';
      this.roleError = e?.message || 'Could not create the role. Please try again.';
    }
    this.creatingRole = false;
  }

  // ── what the tab holds, and the same buttons for do_action ──────────────────────────────────────

  holds(): Record<string, unknown> {
    return {
      roles: this.roleDefs.map((r) => ({ id: r.id, name: r.name, locked: this.isFullAccess(r) })),
      newRoleName: this.newRoleName,
    };
  }

  unsavedHere(): { count: number; name: string } | null {
    if (this.newRoleName.trim()) return { count: 1, name: 'The new role' };
    const renaming = this.roleDefs.find((r) => r.id === this.renamingId);
    if (renaming && this.renameDraft !== renaming.name) return { count: 1, name: `The rename of ${renaming.name}` };
    return null;
  }

  private async createRoleFor(values: Record<string, unknown>): Promise<ScreenActionAnswer> {
    if (this.loading) return { refused: 'The roles are still loading. Nothing was changed; try again in a moment.' };
    if (this.creatingRole) return { refused: 'A role is being created. Nothing was changed; try again in a moment.' };
    if (this.newRoleName.trim()) return { refused: 'The builder is typing a new role; ask them to finish or clear it first. Nothing was changed.' };
    const name = String(values['newRoleName'] ?? '').trim();
    // The button stays disabled on a blank name, so the method never had to say so.
    if (!name && this.createControl.enabled) return { refused: 'Enter a name for the role. Nothing was changed.' };

    this.newRoleName = name;
    await this.createRole();
    if (this.roleError) {
      this.newRoleName = '';
      return { refused: `${this.roleError} Nothing was changed.` };
    }
    const role = this.roleDefs.find((r) => r.name === name);
    return { done: `Created the role ${name}.`, result: { role: { id: role?.id, name } } };
  }

  private async renameRoleFor(values: Record<string, unknown>): Promise<ScreenActionAnswer> {
    if (this.loading) return { refused: 'The roles are still loading. Nothing was changed; try again in a moment.' };
    if (!this.createControl.enabled) return { refused: `${this.createControl.reason} Nothing was changed.` };
    if (this.renamingId !== null) return { refused: 'The builder is renaming a role; ask them to save or cancel it first. Nothing was changed.' };
    const role = this.roleDefs.find((r) => r.id === values['roleId']);
    if (!role) return { refused: `There is no role "${values['roleId']}". Nothing was changed. The roles are: ${this.roleDefs.map((r) => r.id).join(', ')}.` };
    if (this.isFullAccess(role)) return { refused: `${role.name} is locked and can't be renamed. Nothing was changed.` };
    const name = String(values['name'] ?? '');
    if (name.length > 60) return { refused: 'A role name holds 60 characters at most. Nothing was changed.' };

    this.startRename(role);
    this.renameDraft = name;
    await this.saveRename(role);
    const failure = this.renameError;
    this.cancelRename();
    if (failure) return { refused: `${failure} Nothing was changed.` };
    return { done: `Renamed the role to ${name.trim()}.`, result: { role: { id: role.id, name: name.trim() } } };
  }
}
